import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Col,
  Row,
} from 'react-bootstrap';
import FormVisPanel from './FormVisPanel';
import FormSearchRules from './FormSearchRules';
import { saveRules } from 'api';
import { BsGear } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';
import { FaRegThumbsUp } from 'react-icons/fa';
import { BiError } from 'react-icons/bi';
import AppContext from 'Utils/AppContext';
import { INK, STATUS } from 'Utils/styleTokens';
import FetchContainer from 'Utils/FetchContainer';
import PageHeader from 'Utils/PageHeader';

// Pull a useful message out of an ApiError (or any thrown value) for a toast.
const describeApiError = (e) => {
  if (!e) return null;
  const body = e.body;
  if (body && typeof body === 'object') {
    if (body.message) return String(body.message);
    if (body.error) return String(body.error);
  }
  if (e.status) return `Server returned ${e.status}${e.statusText ? ' ' + e.statusText : ''}.`;
  if (e.message) return String(e.message);
  return null;
};

const rulesInit = {
  short_circuit_if: {},
  last: true,
  name: "Sample Search",
  minimum_sampled_rows: 1000,
  exclude_if: {
      column: {}
  },
  dry_run: false,
  id: "my_sample_search_v1",
  search_all_if: {},
  sample: {
      row: {
          size: 200,
          active: true,
          every: 1000
      }
  }
}

const Rules = (props) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sampleRows, setSampleRows] = useState({});
  const [rules, setRules] = useState(rulesInit);
  const { id } = useParams();
  const { setNotification, rulesMap, isAdmin } = useContext(AppContext);
  const history = useHistory();

  useEffect(() => {
    if (id && id in rulesMap) {
      setRules(rulesMap[id]);
    } else {
      setRules(rulesInit);
    }
  }, [id]);

  async function deleteRules(rules) {
    setFetchingData(true);
    let newRulesList = [...props.rulesList];
    let index;

    for (let i = 0; i < newRulesList.length; i++) {
      if (newRulesList[i].id === rules.id) {
        index = i;
      }
    }

    newRulesList.splice(index, 1);
    try {
      await saveRules(newRulesList);
    } catch (e) {
      setFetchingData(false);
      setNotification({
        header: 'Could not delete sampling rule',
        icon: (<BiError size='25px' color={INK[500]} />),
        body: <span>{describeApiError(e) || 'Please try again.'}</span>
      });
      return;
    }
    await props.fetchNewRules();
    setFetchingData(false);
    history.push('/rules');
  }

  async function saveRulesHandler(rules) {
    setSaving(true);
    let newRulesList = [...props.rulesList];
    let foundRules = false;
    // One global admin-managed list — every preset is editable in place (the PUT endpoint no
    // longer scopes by user). Update the matching row, or append a new one.
    for (let i = 0; i < newRulesList.length; i++) {
      if (newRulesList[i].id === rules.id) {
        Object.assign(newRulesList[i], rules);
        newRulesList[i].last = true;
        foundRules = true;
      } else {
        newRulesList[i].last = false;
      }
    }

    if (!foundRules) {
      rules.last = true;
      rules.id = `${rules.name}_${Date.now()}`;
      newRulesList.push(rules);
    }

    try {
      await saveRules(newRulesList);
    } catch (e) {
      setSaving(false);
      setNotification({
        header: 'Could not save sampling rule',
        icon: (<BiError size='25px' color={INK[500]} />),
        body: <span>{describeApiError(e) || 'Please try again.'}</span>
      });
      return;
    }
    setNotification({
      header: 'Sample Rules saved!',
      icon: (<FaRegThumbsUp size='25px' color={STATUS.success.dot} />),
      body: <span>{rules.name} successfully saved.</span>
    });
    try {
      await props.fetchNewRules();
    } catch (e) {
      // Save itself succeeded; failing to refresh the list shouldn't roll
      // back the success notification, but log so the user has a breadcrumb.
      console.log('failed to refresh sampling rules after save', e);
    }
    setSaving(false);
  }

  return (
    <Col className='p-0'>
      <FetchContainer fetchingData={fetchingData} absolute={true}>
        <PageHeader
          header='Sample Rules Editor'
          tooltip='Create or edit your Sample Rules.'
          icon={BsGear}
        />
        <Row className="widget-row">
          <Col lg="7">
            <Card className="shadow-sm widget-card">
                <div className="widget-header p-3 text-left">
                  <BsGear className="widget-header-icon mr-2" /> Edit Sampling Rules
                </div>
                <Card.Body>
                  <Row>
                    <Col>
                      <FormContainer
                        setSampleRows={(obj) => setSampleRows(obj)}
                        saving={saving}
                        rules={rules}
                        canEdit={isAdmin}
                        fetchingData={fetchingData}
                        deleteRulesHandler={(rules) => deleteRules(rules)}
                        saveRulesHandler={(rules) => saveRulesHandler(rules)}
                      />
                    </Col>
                  </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col lg="5">
            <FormVisPanel
              rules={sampleRows}
            />
          </Col>
        </Row>
      </FetchContainer>
    </Col>
  );
}

const FormContainer = (props) => {
  return (
    <FormSearchRules
      setSampleRows={(obj) => props.setSampleRows(obj)}
      saving={props.saving}
      rules={props.rules}
      canEdit={props.canEdit}
      fetchingData={props.fetchingData}
      deleteRulesHandler={(rules) => props.deleteRulesHandler(rules)}
      saveRulesHandler={(rules) => props.saveRulesHandler(rules)}
    />
  );
}

export default Rules
